import { useMemo, useCallback } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridColumns } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import type { CompletionsPerUserResponse } from "../../types";
import { formatNumber, formatCompact } from "../../utils/formatters";
import styles from "./CompletionsChart.module.css";

type UserEntry = CompletionsPerUserResponse["users"][number];

const MAX_BARS = 15;
const MARGIN = { top: 8, right: 16, bottom: 40, left: 120 };
const BAR_COLOR = "#6366f1";
const BAR_HOVER_COLOR = "#818cf8";

function truncateId(id: string, maxLen = 16): string {
  return id.length > maxLen ? `${id.slice(0, maxLen)}…` : id;
}

interface InnerChartProps {
  users: UserEntry[];
  width: number;
  height: number;
}

function InnerChart({ users, width, height }: InnerChartProps) {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<UserEntry>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const yScale = useMemo(
    () =>
      scaleBand<string>({
        domain: users.map((u) => u.workspace_id),
        range: [0, innerHeight],
        padding: 0.25,
      }),
    [users, innerHeight],
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, users.reduce((m, u) => Math.max(m, u.completion_count), 0) * 1.1 || 1],
        range: [0, innerWidth],
        nice: true,
      }),
    [users, innerWidth],
  );

  const handleMouseOver = useCallback(
    (entry: UserEntry) => {
      const barY = yScale(entry.workspace_id) ?? 0;
      const barHeight = yScale.bandwidth();
      showTooltip({
        tooltipData: entry,
        tooltipLeft: MARGIN.left + xScale(entry.completion_count),
        tooltipTop: MARGIN.top + barY + barHeight / 2,
      });
    },
    [yScale, xScale, showTooltip],
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  return (
    <div className={styles.chartWrapper}>
      <svg width={width} height={height} role="img" aria-label="Completions per user">
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridColumns
            scale={xScale}
            height={innerHeight}
            stroke="var(--colours-neutral-100, #f3f4f6)"
          />
          {users.map((entry) => {
            const barWidth = xScale(entry.completion_count);
            const barY = yScale(entry.workspace_id) ?? 0;
            const barHeight = yScale.bandwidth();
            const isHovered = tooltipData?.workspace_id === entry.workspace_id;
            return (
              <rect
                key={entry.workspace_id}
                x={0}
                y={barY}
                width={Math.max(barWidth, 0)}
                height={barHeight}
                fill={isHovered ? BAR_HOVER_COLOR : BAR_COLOR}
                rx={3}
                onMouseMove={() => handleMouseOver(entry)}
                onMouseLeave={hideTooltip}
              />
            );
          })}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={5}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickFormat={(v) => formatCompact(v as number)}
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            })}
            label="Completions"
            labelOffset={20}
            labelProps={{
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
          <AxisLeft
            scale={yScale}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickFormat={(v) => truncateId(v as string)}
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "end" as const,
              dx: -4,
              dy: 4,
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipLabel}>{tooltipData.workspace_id}</span>
            <span>
              <strong>{formatNumber(tooltipData.completion_count)}</strong> completions
            </span>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

export interface CompletionsChartProps {
  data: CompletionsPerUserResponse;
}

const ROW_HEIGHT = 28;

export function CompletionsChart({ data }: CompletionsChartProps) {
  const sorted = useMemo(
    () =>
      [...data.users]
        .sort((a, b) => b.completion_count - a.completion_count)
        .slice(0, MAX_BARS),
    [data.users],
  );

  if (sorted.length === 0) {
    return (
      <div className={styles.emptyState}>No completions data for this period.</div>
    );
  }

  const chartHeight = Math.max(sorted.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom, 200);

  return (
    <div className={styles.container}>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total</span>
          <span className={styles.statValue}>{formatNumber(data.total_completions)}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Users</span>
          <span className={styles.statValue}>{formatNumber(data.users.length)}</span>
        </div>
      </div>
      <ParentSize>
        {({ width }) => (
          <InnerChart users={sorted} width={width} height={chartHeight} />
        )}
      </ParentSize>
    </div>
  );
}
